import _ from 'lodash'
import bwipjs from 'bwip-js'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'

import type { UI2BarcodeDisplayProps } from 'shared-libs/components/ui2/fields/ui2-barcode-display/ui2-barcode-display-props'
import type { IUI2WebFieldBaseExtras } from '../field-ui/i-ui2-web-field-base-extras'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { pointsToMillimeters } from 'shared-libs/helpers/mobile/util'

import './_ui2-barcode-display.scss'

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_BWIP_OPTIONS: Partial<bwipjs.RenderOptions> = {
  bcid: 'code128',
  height: 10,
  includetext: true,
  textxalign: 'center',
}

const DEFAULT_EMPTY_TEXT = 'Barcode not available'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface IUI2BarcodeDisplayProps extends UI2BarcodeDisplayProps, IUI2WebFieldBaseExtras {}

// ─── Component ────────────────────────────────────────────────────────────────

export const UI2BarcodeDisplay: React.FC<IUI2BarcodeDisplayProps> = (props) => {
  const { value, barcodeValue, renderOptions } = props

  const displayValue =
    barcodeValue ??
    renderOptions?.text ??
    (value != null && value !== '' ? String(value) : undefined)

  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lightboxCanvasRef = useRef<HTMLCanvasElement>(null)

  const drawToCanvas = useCallback(
    (canvas: HTMLCanvasElement | null, overrides?: Partial<bwipjs.RenderOptions>) => {
      if (!displayValue || !canvas) return
      const el = containerRef.current
      const containerWidthPx = el?.getBoundingClientRect().width ?? 0
      const mm =
        containerWidthPx > 0
          ? pointsToMillimeters(containerWidthPx, window.devicePixelRatio || 1)
          : undefined
      const scale =
        mm && !renderOptions?.scale && !renderOptions?.width ? { width: _.clamp(mm, 35, 80) } : {}
      const options = _.merge({}, DEFAULT_BWIP_OPTIONS, scale, renderOptions, overrides, {
        text: displayValue,
      })
      try {
        bwipjs.toCanvas(canvas, options as bwipjs.RenderOptions)
      } catch {
        // Invalid barcode value — leave canvas blank.
      }
    },
    [displayValue, renderOptions]
  )

  useEffect(() => {
    drawToCanvas(canvasRef.current)
  }, [drawToCanvas])

  useEffect(() => {
    if (isLightboxOpen) drawToCanvas(lightboxCanvasRef.current, { width: 80, scale: 2 })
  }, [isLightboxOpen, drawToCanvas])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setIsLightboxOpen(false)
  }, [])

  useEffect(() => {
    if (isLightboxOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isLightboxOpen, handleKeyDown])

  const renderBody = useCallback(() => {
    if (!displayValue) return null
    return (
      <div ref={containerRef} className="ui2-barcodeDisplay-container">
        <button
          className="ui2-barcodeDisplay-tile"
          // Zooming is an interaction like any other — a disabled display
          // field shows its code but doesn't open.
          onClick={props.isDisabled ? undefined : () => setIsLightboxOpen(true)}
          disabled={!!props.isDisabled}
          aria-label="Open barcode"
        >
          <canvas ref={canvasRef} />
          <span className="ui2-barcodeDisplay-zoomBadge">
            <Icon icon={IconNames.ZOOM_IN} iconSize={12} />
          </span>
        </button>
      </div>
    )
  }, [displayValue, props.isDisabled])

  const lightbox = isLightboxOpen
    ? ReactDOM.createPortal(
        <div className="ui2-barcodeDisplay-lightbox" role="dialog" aria-modal>
          <div
            className="ui2-barcodeDisplay-lightbox-backdrop"
            onClick={() => setIsLightboxOpen(false)}
          />
          <div className="ui2-barcodeDisplay-lightbox-content">
            <canvas ref={lightboxCanvasRef} />
          </div>
          <button
            className="ui2-barcodeDisplay-lightbox-close"
            onClick={() => setIsLightboxOpen(false)}
            aria-label="Close"
          >
            <Icon icon={IconNames.CROSS} />
          </button>
        </div>,
        document.body
      )
    : null

  return (
    <>
      <UI2FieldShell
        {...props}
        isReadOnly
        isValueEmpty={() => !displayValue}
        emptyText={props.emptyText ?? DEFAULT_EMPTY_TEXT}
        renderReadOnly={(_value, { editNode }) => {
          const body = renderBody()
          return body ? <><div className='ui2-fieldShell-readOnly-content'>{body}</div>{editNode}</> : null
        }}
        className="ui2-barcode-display"
      />
      {lightbox}
    </>
  )
}
